import React, { useState, useEffect, useContext } from "react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
  Autocomplete,
} from "@mui/material";
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { X } from "lucide-react";
import { AuthContext } from "../../../context/Auth/AuthContext";
import { showSuccess, showError } from "../../../utils/toast";
import getClientChasingSummary from "../../../api/getClientChasingSummary";
import getActiveEmailTemplates from "../../../api/getActiveEmailTemplates";
import previewChasingEmail from "../../../api/previewChasingEmail";
import sendChasingEmail from "../../../api/sendChasingEmail";

const schema = yup.object().shape({
  template: yup.object().required("Template is required").nullable(),
  recipientEmail: yup
    .string()
    .required("Recipient email is required")
    .email("Enter a valid email address"),
});

// Compact, single-document Send Email popup — reuses the exact same chasing
// APIs/logic as the full DocumentChasingPage (scoped via document_request_id),
// with no client-level totals, pending list, or history — just what's needed
// to send one reminder for the document the user clicked.
const DocumentChasingSendPopup = ({
  open,
  onClose,
  clientID,
  documentRequestId,
  documentName,
}) => {
  const [summary, setSummary] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [sending, setSending] = useState(false);

  const { token } = useContext(AuthContext);

  const {
    handleSubmit,
    control,
    reset,
    setValue,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      template: null,
      recipientEmail: "",
      subjectPreview: "",
      bodyPreview: "",
    },
  });

  useEffect(() => {
    if (!open) return;
    reset();

    const loadData = async () => {
      const [summaryRes, templatesRes] = await Promise.all([
        getClientChasingSummary({ token, contact_id: clientID }),
        getActiveEmailTemplates({ token }),
      ]);
      if (summaryRes?.code === 200) {
        setSummary(summaryRes.data);
        setValue("recipientEmail", summaryRes.data.clientEmail || "");
      }
      if (templatesRes?.code === 200) {
        setTemplates(templatesRes.data || []);
      }
    };
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, clientID]);

  const handleTemplateChange = async (selectedTemplate) => {
    if (!selectedTemplate) {
      setValue("subjectPreview", "");
      setValue("bodyPreview", "");
      return;
    }
    const res = await previewChasingEmail({
      token,
      contact_id: clientID,
      template_id: selectedTemplate.id,
      document_request_id: documentRequestId,
    });
    if (res?.code === 200) {
      setValue("subjectPreview", res.data.subject);
      setValue("bodyPreview", res.data.body);
    } else {
      showError("Unable to generate preview.");
    }
  };

  const onSubmit = async (data) => {
    setSending(true);
    try {
      const response = await sendChasingEmail({
        token,
        contact_id: clientID,
        template_id: data.template.id,
        recipient_email: data.recipientEmail,
        document_request_id: documentRequestId,
      });
      if (response?.code === 200) {
        showSuccess("Reminder email sent successfully.");
        onClose();
      } else {
        showError(response?.error || "Error while sending reminder email.");
      }
    } catch (err) {
      showError("Error while sending reminder email.");
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ style: { borderRadius: "8px" } }}
    >
      <div className="flex gap-3 items-center px-4 py-2 relative">
        <DialogTitle className="text-[#1a6b7c] !text-2xl font-medium p-0">
          Document Chasing
        </DialogTitle>
        <X
          size={24}
          onClick={onClose}
          className="bg-[#1a6b7c] rounded-full p-1 text-white cursor-pointer absolute right-4 top-3"
        />
      </div>

      <DialogContent className="h-auto">
        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
          Client Summary
        </Typography>
        <div className="grid grid-cols-2 gap-2 text-sm mb-4">
          <div>Client Name: {summary?.clientName || "-"}</div>
          <div>Selected Document: {documentName || "-"}</div>
          <div>
            Last Request Date:{" "}
            {summary?.lastRequestDate
              ? new Date(summary.lastRequestDate).toLocaleDateString()
              : "-"}
          </div>
          <div>
            Last Received Date:{" "}
            {summary?.lastReceivedDate
              ? new Date(summary.lastReceivedDate).toLocaleDateString()
              : "-"}
          </div>
        </div>

        <form onSubmit={handleSubmit(onSubmit)}>
          <Controller
            name="template"
            control={control}
            render={({ field }) => (
              <Autocomplete
                options={templates}
                getOptionLabel={(option) => option?.name || ""}
                isOptionEqualToValue={(option, value) => option?.id === value?.id}
                value={field.value || null}
                onChange={(e, value) => {
                  field.onChange(value);
                  handleTemplateChange(value);
                }}
                renderOption={(props, option) => (
                  <li {...props} key={option.id}>
                    {option.name}
                  </li>
                )}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Email Template"
                    variant="outlined"
                    margin="normal"
                    error={!!errors.template}
                    helperText={errors.template?.message}
                  />
                )}
              />
            )}
          />

          <Controller
            name="recipientEmail"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                label="Recipient Email"
                fullWidth
                margin="normal"
                variant="outlined"
                error={!!errors.recipientEmail}
                helperText={errors.recipientEmail?.message}
              />
            )}
          />

          <Controller
            name="subjectPreview"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                label="Subject"
                fullWidth
                margin="normal"
                variant="outlined"
                InputProps={{ readOnly: true }}
              />
            )}
          />

          <Controller
            name="bodyPreview"
            control={control}
            render={({ field }) => (
              <div className="mt-2">
                <Typography variant="body2" color="text.secondary">
                  Body
                </Typography>
                <div
                  className="border rounded p-2 text-sm"
                  style={{ minHeight: "100px", maxHeight: "200px", overflowY: "auto" }}
                  dangerouslySetInnerHTML={{ __html: field.value || "" }}
                />
              </div>
            )}
          />

          <DialogActions sx={{ px: 0, mt: 2 }}>
            <Button onClick={onClose} color="inherit">
              Cancel
            </Button>
            <Button type="submit" color="primary" variant="contained" disabled={sending}>
              Send Email
            </Button>
          </DialogActions>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default DocumentChasingSendPopup;
